import { useState } from "react";
import { Loader2, Lock, Download, Trash2, ShieldCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { fetchAnalytics, previewCleanup, runCleanup, SEVERITY_COLORS } from "@/lib/api";
import { BrandLockup } from "@/components/BrandLockup";

const SEVERITY_LABELS = { low: "Low", moderate: "Moderate", high: "High", critical: "Critical" };
const FB_LABEL = {
  very_accurate: "Very accurate", mostly_accurate: "Mostly accurate", somewhat_accurate: "Somewhat accurate", not_accurate: "Not accurate",
  yes: "Yes", partially: "Partially", no: "No",
  very_useful: "Very useful", useful: "Useful", somewhat_useful: "Somewhat useful", not_useful: "Not useful",
};
const HOURS_LABEL = { lt_2: "< 2", "2_5": "2–5", "6_10": "6–10", "11_20": "11–20", gt_20: "> 20" };
const fmt = (v, map) => (v == null ? "—" : (map ? map[v] || v : v));

const ACCURACY_ORDER = ["very_accurate", "mostly_accurate", "somewhat_accurate", "not_accurate"];
const USEFUL_ORDER = ["very_useful", "useful", "somewhat_useful", "not_useful"];
const REAL_ORDER = ["yes", "partially", "no"];

// Protocol names map deterministically from the existing primary bottleneck key.
const PROTOCOL_LABELS = {
  decision: "Decision Rights Architecture",
  approval: "Approval Elimination Protocol",
  problem: "Recommendation-First Protocol",
  information: "Knowledge Independence System",
  operational: "Founder Replacement Test",
  relationship: "Relationship Transfer System",
};

function scoreColor(score) {
  if (score >= 75) return "#8C3333";
  if (score >= 50) return "#A96143";
  if (score >= 25) return "#A68A56";
  return "#475F51";
}

const Stat = ({ label, value }) => (
  <div className="bg-white border border-[#E2DFD8] p-6">
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#A68A56]">{label}</p>
    <p className="font-display text-3xl font-light text-[#0B0E14] mt-2 tabular-nums">{value}</p>
  </div>
);

const Panel = ({ title, children, testId }) => (
  <div className="bg-white border border-[#E2DFD8] p-6" data-testid={testId}>
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8A6F3E] mb-5">{title}</p>
    {children}
  </div>
);

const BreakdownBars = ({ counts, order, labelMap, total }) => (
  <div className="flex flex-col gap-3">
    {order.map((k) => {
      const n = counts[k] || 0;
      const pct = total ? (n / total) * 100 : 0;
      return (
        <div key={k}>
          <div className="flex items-baseline justify-between mb-1 text-sm">
            <span className="text-[#4A5568]">{labelMap[k]}</span>
            <span className="tabular-nums text-[#0B0E14]">{n}</span>
          </div>
          <div className="h-2 w-full bg-[#F0EEE9]">
            <div className="h-full bg-[#1E293B]" style={{ width: `${pct}%`, transition: "width 0.6s ease-out" }} />
          </div>
        </div>
      );
    })}
  </div>
);

const Select = ({ label, value, onChange, options, testId }) => (
  <label className="flex flex-col gap-1.5">
    <span className="text-xs uppercase tracking-[0.16em] text-[#718096]">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="bg-white border border-[#E2DFD8] px-3 py-2.5 text-sm text-[#0B0E14] focus:outline-none focus:border-[#0B0E14] transition-colors min-w-[150px]"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  </label>
);

function toCSV(rows, dimension_order, dimension_labels) {
  const header = [
    "Tester ID", "Date", "Language", "Index", "Level",
    ...dimension_order.map((k) => dimension_labels[k]),
    "Primary Bottleneck", "Protocol", "Hours/Week", "Continuity (1-5)",
    "Feedback Accuracy", "Feedback Usefulness", "Identified Real",
    "Journal Days", "Journal Interventions", "Journal Completed", "Comment",
  ];
  const body = rows.map((r) => [
    r.tester_id, r.date, r.language, r.index, SEVERITY_LABELS[r.severity_key],
    ...dimension_order.map((k) => r.dimension_scores[k]),
    r.primary_name, PROTOCOL_LABELS[r.primary_key] || "—", fmt(r.baseline_hours, HOURS_LABEL), fmt(r.continuity_score),
    fmt(r.feedback_accuracy, FB_LABEL), fmt(r.feedback_usefulness, FB_LABEL),
    fmt(r.feedback_identified_real, FB_LABEL),
    r.journal_days, r.journal_interventions, r.journal_completed ? "Yes" : "No",
    r.feedback_comment || "",
  ]);
  const esc = (v) => {
    let s = String(v ?? "");
    if (/^[\s\uFEFF]*[=+\-@\t\r]/.test(s)) s = "'" + s; // neutralize CSV formula injection (incl. leading whitespace/BOM)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [header, ...body].map((row) => row.map(esc).join(",")).join("\n");
}

export default function Owner() {
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [filters, setFilters] = useState({ language: "all", level: "all", primary: "all" });
  const [cleanup, setCleanup] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async (tkn) => {
    const useToken = tkn || token;
    setLoading(true);
    setErr("");
    try {
      const [d, c] = await Promise.all([fetchAnalytics(useToken), previewCleanup(useToken)]);
      setData(d);
      setCleanup(c);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Invalid access token." : "Could not load analytics.");
    } finally {
      setLoading(false);
    }
  };

  const doCleanup = async () => {
    setDeleting(true);
    try {
      const res = await runCleanup(token);
      toast.success(`Removed ${res.deleted} test record(s). ${res.remaining} real assessment(s) remain.`);
      setConfirmText("");
      await load(token);
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Session expired — reload and re-enter your token." : "Cleanup failed. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const exportCSV = (rows, dimension_order, dimension_labels) => {
    const csv = toCSV(rows, dimension_order, dimension_labels);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `founder-bottleneck-testers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F6F5F2] flex items-center justify-center px-6">
        <div className="w-full max-w-sm bg-white border border-[#E2DFD8] p-8 md:p-10">
          <div className="mb-8" data-testid="brand-header">
            <BrandLockup />
          </div>
          <div className="w-10 h-10 border border-[#E2DFD8] flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-[#A68A56]" strokeWidth={1.5} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#8A6F3E] mb-2">Private</p>
          <h1 className="font-display text-2xl font-normal text-[#0B0E14] mb-6">Owner Analytics</h1>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Access token"
            data-testid="owner-token-input"
            className="w-full bg-[#F6F5F2] border border-[#E2DFD8] p-4 text-base text-[#0B0E14] placeholder:text-[#A0AEC0] focus:outline-none focus:border-[#0B0E14] transition-colors"
          />
          {err && <p className="text-sm text-[#8C3333] mt-3" data-testid="owner-error">{err}</p>}
          <button
            onClick={() => load()}
            disabled={loading || !token.trim()}
            data-testid="owner-unlock-btn"
            className="mt-6 w-full bg-[#0B0E14] text-white px-8 py-4 text-sm font-medium tracking-wide hover:bg-[#2C363F] transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            View Analytics
          </button>
        </div>
      </div>
    );
  }

  const { overview, insights, testers, dimension_order, dimension_labels } = data;

  const distData = ["low", "moderate", "high", "critical"].map((k) => ({
    key: k, name: SEVERITY_LABELS[k], value: overview.distribution[k], color: SEVERITY_COLORS[k],
  }));
  const dimData = dimension_order.map((k) => ({
    key: k, name: dimension_labels[k].split(" ")[0], score: insights.avg_dimension[k], color: scoreColor(insights.avg_dimension[k]),
  }));
  const fbTotal = overview.feedback_count;
  const protocolCounts = testers.reduce((acc, row) => {
    acc[row.primary_key] = (acc[row.primary_key] || 0) + 1;
    return acc;
  }, {});

  const languagesPresent = Array.from(new Set(testers.map((t) => t.language)));
  const filtered = testers.filter((r) =>
    (filters.language === "all" || r.language === filters.language) &&
    (filters.level === "all" || r.severity_key === filters.level) &&
    (filters.primary === "all" || r.primary_key === filters.primary)
  );

  const canDelete = confirmText.trim().toUpperCase() === "DELETE" && cleanup && cleanup.deletable_test > 0;

  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      <header className="border-b border-[#E2DFD8] bg-[#F6F5F2]/95 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <span data-testid="brand-header">
            <BrandLockup suffix=" · Owner" />
          </span>
          <div className="flex items-center gap-5">
            <button
              onClick={() => exportCSV(filtered, dimension_order, dimension_labels)}
              data-testid="export-csv-btn"
              className="inline-flex items-center gap-2 border border-[#0B0E14] text-[#0B0E14] px-5 py-2.5 text-xs font-medium tracking-wide hover:bg-[#0B0E14] hover:text-white transition-colors"
            >
              <Download className="w-4 h-4" strokeWidth={1.75} />
              Export CSV
            </button>
            <span className="text-xs uppercase tracking-[0.18em] text-[#718096] hidden md:inline">Private analytics</span>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="owner-overview">
          <Stat label="Assessments" value={overview.total_assessments} />
          <Stat label="Avg. Index" value={overview.average_index} />
          <Stat label="Feedback" value={overview.feedback_count} />
          <Stat label="Top Bottleneck" value={overview.most_common_primary ? overview.most_common_primary.name : "—"} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4" data-testid="owner-charts">
          <Panel title="Dependency-level distribution" testId="chart-distribution">
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart data={distData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#718096" }} axisLine={{ stroke: "#E2DFD8" }} tickLine={false} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#F0EEE9" }} contentStyle={{ border: "1px solid #E2DFD8", borderRadius: 0, fontSize: 12 }} />
                  <Bar dataKey="value" radius={0}>
                    {distData.map((d) => <Cell key={d.key} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
          <Panel title="Average score by dimension" testId="chart-dimensions">
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer minWidth={0} minHeight={0}>
                <BarChart data={dimData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#718096" }} axisLine={{ stroke: "#E2DFD8" }} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#F0EEE9" }} contentStyle={{ border: "1px solid #E2DFD8", borderRadius: 0, fontSize: 12 }} />
                  <Bar dataKey="score" radius={0}>
                    {dimData.map((d) => <Cell key={d.key} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>
        </div>

        <div className="mt-4">
          <Panel title="Intervention protocols applied" testId="chart-protocols">
            <BreakdownBars counts={protocolCounts} order={dimension_order} labelMap={PROTOCOL_LABELS} total={testers.length} />
          </Panel>
        </div>

        <h2 className="font-display text-2xl font-normal text-[#0B0E14] mt-16 mb-6">Feedback insights</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="owner-insights">
          <Panel title="Diagnosis accuracy" testId="insight-accuracy">
            <BreakdownBars counts={insights.accuracy} order={ACCURACY_ORDER} labelMap={FB_LABEL} total={fbTotal} />
          </Panel>
          <Panel title="Action usefulness" testId="insight-usefulness">
            <BreakdownBars counts={insights.usefulness} order={USEFUL_ORDER} labelMap={FB_LABEL} total={fbTotal} />
          </Panel>
          <Panel title="Identified a real bottleneck" testId="insight-identified">
            <BreakdownBars counts={insights.identified_real} order={REAL_ORDER} labelMap={FB_LABEL} total={fbTotal} />
          </Panel>
        </div>

        <div className="mt-4">
          <Panel title={`Free-text comments (${insights.comments.length})`} testId="owner-comments">
            {insights.comments.length === 0 ? (
              <p className="text-sm text-[#718096]">No comments submitted yet.</p>
            ) : (
              <div className="flex flex-col divide-y divide-[#E2DFD8]">
                {insights.comments.map((c, i) => (
                  <div key={i} className="py-4 first:pt-0" data-testid={`comment-${c.tester_id}`}>
                    <div className="flex items-center gap-3 mb-1 text-xs text-[#718096]">
                      <span className="font-mono">{c.tester_id}</span>
                      <span className="uppercase">{c.language}</span>
                      <span>{c.date}</span>
                    </div>
                    <p className="text-sm text-[#2C363F] leading-relaxed whitespace-pre-wrap">{c.text}</p>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        {/* Tester results + filters */}
        <div className="mt-16 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <h2 className="font-display text-2xl font-normal text-[#0B0E14]">Tester results</h2>
          <div className="flex flex-wrap gap-4" data-testid="owner-filters">
            <Select
              label="Language" testId="filter-language" value={filters.language}
              onChange={(v) => setFilters((f) => ({ ...f, language: v }))}
              options={[{ value: "all", label: "All" }, ...languagesPresent.map((l) => ({ value: l, label: l.toUpperCase() }))]}
            />
            <Select
              label="Level" testId="filter-level" value={filters.level}
              onChange={(v) => setFilters((f) => ({ ...f, level: v }))}
              options={[{ value: "all", label: "All" }, ...["low", "moderate", "high", "critical"].map((k) => ({ value: k, label: SEVERITY_LABELS[k] }))]}
            />
            <Select
              label="Primary bottleneck" testId="filter-primary" value={filters.primary}
              onChange={(v) => setFilters((f) => ({ ...f, primary: v }))}
              options={[{ value: "all", label: "All" }, ...dimension_order.map((k) => ({ value: k, label: dimension_labels[k] }))]}
            />
          </div>
        </div>

        <div className="overflow-x-auto border border-[#E2DFD8] bg-white">
          <table className="w-full text-sm border-collapse" data-testid="owner-table">
            <thead>
              <tr className="text-left text-[#718096] border-b border-[#E2DFD8]">
                <th className="p-3 font-medium whitespace-nowrap">Tester</th>
                <th className="p-3 font-medium whitespace-nowrap">Date</th>
                <th className="p-3 font-medium">Lang</th>
                <th className="p-3 font-medium whitespace-nowrap">Index</th>
                <th className="p-3 font-medium">Level</th>
                {dimension_order.map((k) => (
                  <th key={k} className="p-3 font-medium whitespace-nowrap" title={dimension_labels[k]}>
                    {dimension_labels[k].split(" ")[0]}
                  </th>
                ))}
                <th className="p-3 font-medium whitespace-nowrap">Primary</th>
                <th className="p-3 font-medium whitespace-nowrap">Protocol</th>
                <th className="p-3 font-medium whitespace-nowrap">Hrs/wk</th>
                <th className="p-3 font-medium whitespace-nowrap">Continuity</th>
                <th className="p-3 font-medium whitespace-nowrap">Accuracy</th>
                <th className="p-3 font-medium whitespace-nowrap">Useful</th>
                <th className="p-3 font-medium whitespace-nowrap">Real?</th>
                <th className="p-3 font-medium whitespace-nowrap">Journal</th>
                <th className="p-3 font-medium whitespace-nowrap">Interv.</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={20} className="p-6 text-center text-[#718096]">No assessments match these filters.</td></tr>
              )}
              {filtered.map((row) => (
                <tr key={row.tester_id} className="border-b border-[#E2DFD8] last:border-0 text-[#2C363F]" data-testid={`owner-row-${row.tester_id}`}>
                  <td className="p-3 font-mono text-xs whitespace-nowrap">{row.tester_id}</td>
                  <td className="p-3 whitespace-nowrap">{row.date}</td>
                  <td className="p-3 uppercase">{row.language}</td>
                  <td className="p-3 font-display tabular-nums">{row.index}</td>
                  <td className="p-3">
                    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                      <span className="w-2 h-2" style={{ backgroundColor: SEVERITY_COLORS[row.severity_key] }} />
                      {SEVERITY_LABELS[row.severity_key]}
                    </span>
                  </td>
                  {dimension_order.map((k) => (
                    <td key={k} className={`p-3 tabular-nums ${k === row.primary_key ? "font-semibold text-[#0B0E14]" : ""}`}>
                      {row.dimension_scores[k]}
                    </td>
                  ))}
                  <td className="p-3 whitespace-nowrap">{row.primary_name}</td>
                  <td className="p-3 whitespace-nowrap text-[#4A5568]" data-testid={`protocol-${row.tester_id}`}>{PROTOCOL_LABELS[row.primary_key] || "—"}</td>
                  <td className="p-3 whitespace-nowrap">{fmt(row.baseline_hours, HOURS_LABEL)}</td>
                  <td className="p-3 tabular-nums">{fmt(row.continuity_score)}</td>
                  <td className="p-3 whitespace-nowrap">{fmt(row.feedback_accuracy, FB_LABEL)}</td>
                  <td className="p-3 whitespace-nowrap">{fmt(row.feedback_usefulness, FB_LABEL)}</td>
                  <td className="p-3 whitespace-nowrap">{fmt(row.feedback_identified_real, FB_LABEL)}</td>
                  <td className="p-3 whitespace-nowrap" data-testid={`journal-${row.tester_id}`}>
                    <span className={row.journal_completed ? "text-[#475F51] font-medium" : row.journal_started ? "text-[#0B0E14]" : "text-[#718096]"}>
                      {row.journal_days}/5{row.journal_completed ? " ✓" : ""}
                    </span>
                  </td>
                  <td className="p-3 tabular-nums">{row.journal_interventions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#4A5568] mt-3">
          Showing {filtered.length} of {testers.length} assessment(s). Anonymous identifiers only — no names or emails are collected.
        </p>

        {/* Data cleanup */}
        <div className="mt-16 border border-[#E2DFD8] bg-white p-6 md:p-8" data-testid="owner-cleanup">
          <div className="flex items-start gap-3 mb-4">
            <Trash2 className="w-5 h-5 text-[#8C3333] mt-0.5 shrink-0" strokeWidth={1.75} />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8C3333]">Data cleanup</p>
              <h3 className="font-display text-xl font-normal text-[#0B0E14] mt-1">Start the pilot with a clean dataset</h3>
            </div>
          </div>
          <p className="text-sm text-[#4A5568] leading-relaxed max-w-2xl">
            Real pilot assessments are submitted through the diagnostic and are protected. Only seed/test
            records (created outside the live flow) can be removed. This action is permanent.
          </p>

          {cleanup && (
            <div className="grid grid-cols-3 gap-4 mt-6 max-w-md" data-testid="cleanup-preview">
              <div className="border border-[#E2DFD8] p-4">
                <p className="text-xs uppercase tracking-[0.14em] text-[#718096]">Total</p>
                <p className="font-display text-2xl text-[#0B0E14] tabular-nums mt-1">{cleanup.total}</p>
              </div>
              <div className="border border-[#E2DFD8] p-4">
                <p className="text-xs uppercase tracking-[0.14em] text-[#475F51] inline-flex items-center gap-1">
                  <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} /> Real
                </p>
                <p className="font-display text-2xl text-[#475F51] tabular-nums mt-1" data-testid="cleanup-protected">{cleanup.protected_real}</p>
              </div>
              <div className="border border-[#E2DFD8] p-4">
                <p className="text-xs uppercase tracking-[0.14em] text-[#8C3333]">Test</p>
                <p className="font-display text-2xl text-[#8C3333] tabular-nums mt-1" data-testid="cleanup-deletable">{cleanup.deletable_test}</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row sm:items-end gap-3">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs uppercase tracking-[0.14em] text-[#718096]">Type DELETE to confirm</span>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                data-testid="cleanup-confirm-input"
                className="bg-[#F6F5F2] border border-[#E2DFD8] px-4 py-2.5 text-sm text-[#0B0E14] placeholder:text-[#A0AEC0] focus:outline-none focus:border-[#8C3333] transition-colors w-48"
              />
            </label>
            <button
              onClick={doCleanup}
              disabled={!canDelete || deleting}
              data-testid="cleanup-delete-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#8C3333] text-white px-6 py-3 text-sm font-medium tracking-wide hover:bg-[#732929] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.75} />}
              Delete {cleanup ? cleanup.deletable_test : 0} test record(s)
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
